(() => {
  const normalizeSize = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/-/g, "/");

  const sizeRank = (value) => {
    const normalized = normalizeSize(value);
    const ranks = {
      xxxs: 0,
      xxs: 10,
      xs: 20,
      "xs/s": 25,
      s: 30,
      "s/m": 35,
      m: 40,
      "m/l": 45,
      l: 50,
      "l/xl": 55,
      xl: 60,
      xxl: 70,
      xxxl: 80
    };

    if (Object.hasOwn(ranks, normalized)) return ranks[normalized];

    const numeric = normalized.match(/\d+(?:[.,]\d+)?/);
    return numeric ? 100 + Number.parseFloat(numeric[0].replace(",", ".")) : null;
  };

  const isUniversalSize = (value) =>
    [
      "universal",
      "universalone",
      "onesize",
      "one/size",
      "jedenrozmiar",
      "uniwersalny",
      "uniwersalna",
      "unica",
      "unico",
      "tailleunique",
      "einheitsgrosse"
    ].includes(normalizeSize(value));

  const preselectComplementarySizes = (root = document) => {
    root.querySelectorAll("[data-cart-complementary-form]").forEach((form) => {
      const radios = Array.from(
        form.querySelectorAll("[data-cart-complementary-variant]")
      );
      const button = form.querySelector("[data-cart-complementary-add]");

      if (!radios.length) {
        if (form.dataset.defaultVariantId && button) button.disabled = false;
        return;
      }

      let selected = radios.find((radio) => radio.checked);
      const sourceSize = normalizeSize(form.dataset.sourceSize);

      if (!selected && sourceSize) {
        selected = radios.find(
          (radio) => normalizeSize(radio.dataset.sizeValue) === sourceSize
        );
      }

      if (!selected) {
        selected = radios.find((radio) => isUniversalSize(radio.dataset.sizeValue));
      }

      if (!selected && sourceSize) {
        const sourceRank = sizeRank(sourceSize);

        if (sourceRank !== null) {
          selected = [...radios].sort((first, second) => {
            const firstRank = sizeRank(first.dataset.sizeValue);
            const secondRank = sizeRank(second.dataset.sizeValue);
            const firstDistance =
              firstRank === null ? Number.POSITIVE_INFINITY : Math.abs(firstRank - sourceRank);
            const secondDistance =
              secondRank === null ? Number.POSITIVE_INFINITY : Math.abs(secondRank - sourceRank);

            if (firstDistance !== secondDistance) return firstDistance - secondDistance;

            const firstIsSmaller = firstRank !== null && firstRank < sourceRank ? 1 : 0;
            const secondIsSmaller = secondRank !== null && secondRank < sourceRank ? 1 : 0;
            return firstIsSmaller - secondIsSmaller;
          })[0];
        }
      }

      selected ||= radios[0];
      selected.checked = true;
      if (button) button.disabled = false;
    });
  };

  const deduplicateComplementaryProducts = (root = document) => {
    const seen = new Set();
    root.querySelectorAll("[data-complementary-product-id]").forEach((product) => {
      const id = product.dataset.complementaryProductId;
      if (seen.has(id)) product.remove();
      else seen.add(id);
    });

    root.querySelectorAll("[data-cart-complementary-group]").forEach((group) => {
      if (!group.querySelector("[data-complementary-product-id]")) group.remove();
    });
  };

  const prepareComplementaryProducts = (root = document) => {
    deduplicateComplementaryProducts(root);
    preselectComplementarySizes(root);
  };

  document.addEventListener(
    "change",
    (event) => {
      const input = event.target.closest("[data-cart-complementary-variant]");
      if (!input) return;
      const form = input.closest("[data-cart-complementary-form]");
      const button = form?.querySelector("[data-cart-complementary-add]");
      const error = form?.querySelector(".cart-complementary-product__error");
      if (button) button.disabled = false;
      if (error) {
        error.hidden = true;
        error.textContent = "";
      }
    },
    true
  );

  document.addEventListener("click", async (event) => {
    const button = event.target.closest("[data-cart-complementary-add]");
    if (!button) return;
    event.preventDefault();
    if (button.disabled || button.classList.contains("loading")) return;

    const form = button.closest("[data-cart-complementary-form]");
    const selected = form?.querySelector("[data-cart-complementary-variant]:checked");
    const variantId = selected?.value || form?.dataset.defaultVariantId;
    const error = form?.querySelector(".cart-complementary-product__error");
    const label = button.querySelector("span");
    const originalLabel = label?.textContent || "";

    if (!variantId) {
      error.textContent = form.dataset.selectSizeError;
      error.hidden = false;
      return;
    }

    button.classList.add("loading");
    button.disabled = true;
    if (label) label.textContent = button.dataset.addingLabel;

    try {
      const cartDrawer = document.querySelector("cart-drawer");
      const renderContext = cartDrawer?.getRenderContext?.();
      const sections = cartDrawer
        ? cartDrawer.getSectionsToRender().map((section) => section.id)
        : [];
      const rootUrl = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${rootUrl}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          properties: { _added_from_complete_look: "true" },
          sections,
          sections_url: window.location.pathname
        })
      });
      const responseText = await response.text();
      let result;

      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(
          form.dataset.unavailableError ||
            window.cartStrings?.error ||
            "This product could not be added to the cart."
        );
      }

      const items = Array.isArray(result?.items) ? result.items : [result];
      const confirmed = items.some((item) => item && String(item.variant_id ?? item.id) === String(variantId) && Number.isInteger(item.quantity) && item.quantity > 0);
      if (!response.ok || result?.status || result?.ok === false || !confirmed) {
        throw new Error(
          result?.description ||
            form.dataset.unavailableError ||
            window.cartStrings?.error
        );
      }

      document.dispatchEvent(
        new CustomEvent("iig:cart-added", {
          detail: {
            source: "customer",
            context: "complete-look",
            productVariantId: String(variantId),
          },
        })
      );

      if (cartDrawer && (renderContext !== undefined || result.sections)) {
        cartDrawer.setActiveElement(button);
        cartDrawer.renderContents(result, renderContext);
        requestAnimationFrame(() => prepareComplementaryProducts(cartDrawer));
      } else {
        window.location.assign(`${rootUrl}cart`);
      }
      if (button.isConnected) {
        button.disabled = false;
        button.classList.remove('loading');
        if (label) label.textContent = originalLabel;
      }
    } catch (caughtError) {
      error.textContent =
        caughtError.message || form.dataset.unavailableError || window.cartStrings.error;
      error.hidden = false;
      button.disabled = false;
      button.classList.remove("loading");
      if (label) label.textContent = originalLabel;
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => prepareComplementaryProducts());
  } else {
    prepareComplementaryProducts();
  }

  document.addEventListener("cart:change", () => {
    requestAnimationFrame(() => prepareComplementaryProducts());
  });
})();
